import React, { useCallback, useReducer } from 'react';

import { isFilterClearEnabled } from '../../filter-table-helper';
import { filtersTableReducer } from '../../filter-table-reducer';
import {
  FilterTableActionTypes,
  SORT,
  TableFiltersState,
  TABLE_COLUMN,
  SubmitFilterArgs,
  Data,
  TableFilters
} from '../../filter-table-types';
import { getDateWithStartOfDay } from '../../../dates';

export const useTableFilters = (initialState: TableFiltersState) => {
  const [state, dispatch] = useReducer(filtersTableReducer, initialState);

  const { activeColumn, sortByColumn, filters } = state;

  const setInitialData = useCallback(
    (data: Data[]) => dispatch({ type: FilterTableActionTypes.SET_INITIAL_DATA, payload: { data } }),
    []
  );

  const setInitialFiltersAndSort = useCallback(
    (filters: TableFilters, sort?: SORT, sortByColumn?: TABLE_COLUMN, isUsingLocalStorage = true) => {
      dispatch({
        type: FilterTableActionTypes.SET_FILTERS_AND_SORT,
        payload: { filters, sort, sortByColumn, isUsingLocalStorage }
      });
    },
    []
  );

  const handleCloseFilter = useCallback(
    () => dispatch({ type: FilterTableActionTypes.TOGGLE_FILTER_MENU, payload: { filterAnchorElement: null } }),
    []
  );

  const handleOpenFilter = useCallback((event: React.MouseEvent<HTMLElement>, column: TABLE_COLUMN) => {
    dispatch({
      type: FilterTableActionTypes.TOGGLE_FILTER_MENU,
      payload: { filterAnchorElement: event.currentTarget, activeColumn: column }
    });
  }, []);

  const handleClearColumnFilter = useCallback(
    (initialFilters: TableFilters) => {
      if (isFilterClearEnabled(filters, sortByColumn, activeColumn)) {
        dispatch({ type: FilterTableActionTypes.CLEAR_ACTIVE_COLUMN_FILTER, payload: { initialFilters } });
      }
    },
    [filters, sortByColumn, activeColumn]
  );

  const handleClearFilters = useCallback(
    (initialFilters: TableFilters) =>
      dispatch({ type: FilterTableActionTypes.CLEAR_FILTERS, payload: { initialFilters } }),
    []
  );

  const handleSortClick = useCallback((newSort: SORT) => {
    dispatch({ type: FilterTableActionTypes.SET_SORT, payload: { sort: newSort } });
  }, []);

  const handleSubmitExpectedDateSummary = useCallback((fromDate?: Date, toDate?: Date, label?: string) => {
    dispatch({
      type: FilterTableActionTypes.SUBMIT_EXPECTED_DATE_FILTERS,
      payload: { fromDate: fromDate ? getDateWithStartOfDay(fromDate) : undefined, toDate, label }
    });
  }, []);

  const handleSubmit = useCallback(
    ({
      fromValue,
      toValue,
      fromDate,
      toDate,
      fromTime,
      toTime,
      selectedValues,
      searchQuery,
      currentDataSelectOptions,
      checkedFilteredData
    }: SubmitFilterArgs) =>
      dispatch({
        type: FilterTableActionTypes.SUBMIT_FILTERS,
        payload: {
          fromValue,
          toValue,
          fromDate,
          toDate,
          fromTime,
          toTime,
          selectedValues,
          searchQuery,
          currentDataSelectOptions,
          checkedFilteredData
        }
      }),
    []
  );

  return {
    state,
    setInitialData,
    handleCloseFilter,
    handleOpenFilter,
    handleClearColumnFilter,
    handleSortClick,
    handleSubmit,
    setInitialFiltersAndSort,
    handleClearFilters,
    handleSubmitExpectedDateSummary
  };
};
